/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  ICreatePaymentOrderRequestModel,
  IErrorModel,
  IPaymentInvoiceModel,
  IPaymentDetailsModel,
  IPaymentMethodModel,
  IPaymentModel,
  IPaymentOfferModel,
  IPaymentOrderModel,
  IUpdatePaymentOrderRequestModel,
} from "../../models";
import * as Consts from "./consts";
import * as Types from "./types";
import {
  IGetUserSwitchOffersAction,
  IGetUserSwitchOffersFailureAction,
  IGetUserSwitchOffersSuccessAction,
  ISwitchSubscriptionAction,
  ISwitchSubscriptionFailureAction,
  ISwitchSubscriptionSuccessAction,
  ICancelSubscriptionSwitchAction,
  ICancelSubscriptionSwitchSuccessAction,
  ICancelSubscriptionSwitchFailureAction
} from "./types";
import { UrlObject } from "url";

export const getPaymentsInfo = (): Types.IGetPaymentsInfoAction => {
  return {
    type: Consts.GET_PAYMENTS_INFO,
  };
};

export const getPaymentsInfoSuccess = (
  methods: IPaymentMethodModel[],
  offers: IPaymentOfferModel[]
): Types.IGetPaymentsInfoSuccessAction => {
  return {
    type: Consts.GET_PAYMENTS_INFO_SUCCESS,
    payload: { methods, offers },
  };
};

export const getPaymentsInfoFailure = (
  error?: IErrorModel
): Types.IGetPaymentsInfoErrorAction => {
  return {
    error,
    type: Consts.GET_PAYMENTS_INFO_FAILURE,
  };
};

export const createPaymentOrder = (
  data: ICreatePaymentOrderRequestModel
): Types.ICreatePaymentOrderAction => {
  return {
    type: Consts.CREATE_PAYMENT_ORDER,
    data,
  };
};

export const resetPaymentOrder = (): Types.IResetPaymentOrder => {
  return {
    type: Consts.RESET_PAYMENT_ORDER,
  };
};

export const createPaymentOrderSuccess = (
  order: IPaymentOrderModel
): Types.ICreatePaymentOrderSuccessAction => {
  return {
    type: Consts.CREATE_PAYMENT_ORDER_SUCCESS,
    payload: order,
  };
};

export const createPaymentOrderFailure = (
  error?: IErrorModel
): Types.ICreatePaymentOrderErrorAction => {
  return {
    error,
    type: Consts.CREATE_PAYMENT_ORDER_FAILURE,
  };
};

export const updatePaymentOrder = (
  data: IUpdatePaymentOrderRequestModel
): Types.IUpdatePaymentOrderAction => {
  return {
    type: Consts.UPDATE_PAYMENT_ORDER,
    data,
  };
};

export const updatePaymentOrderSuccess = (
  order: IPaymentOrderModel
): Types.IUpdatePaymentOrderSuccessAction => {
  return {
    type: Consts.UPDATE_PAYMENT_ORDER_SUCCESS,
    payload: order,
  };
};

export const updatePaymentOrderFailure = (
  error?: IErrorModel
): Types.IUpdatePaymentOrderErrorAction => {
  return {
    error,
    type: Consts.UPDATE_PAYMENT_ORDER_FAILURE,
  };
};

export const createInitialPayment = (
  orderId: number,
  language: string
): Types.ICreateInitialPaymentAction => {
  return {
    type: Consts.CREATE_INITIAL_PAYMENT,
    payload: { orderId, language },
  };
};

export const createInitialPaymentSuccess = (
  payload: IPaymentModel
): Types.ICreateInitialPaymentSuccessAction => {
  return {
    type: Consts.CREATE_INITIAL_PAYMENT_SUCCESS,
    payload,
  };
};

export const createInitialPaymentWithoutDetails =
  (): Types.ICreateInitialPaymentWithoutPaymentDetailsAction => {
    return {
      type: Consts.CREATE_INITIAL_PAYMENT_WITHOUT_PAYMENT_DETAILS,
    };
  };

export const createInitialPaymentFailure = (
  error?: IErrorModel
): Types.ICreateInitialPaymentErrorAction => {
  return {
    error,
    type: Consts.CREATE_INITIAL_PAYMENT_FAILURE,
  };
};

export const getPaymentInvoices = (): Types.IGetPaymentInvoicesAction => {
  return {
    type: Consts.GET_PAYMENT_INVOICES,
  };
};

export const getPaymentInvoicesSuccess = (
  payload: IPaymentInvoiceModel[]
): Types.IGetPaymentInvoicesSuccessAction => {
  return {
    type: Consts.GET_PAYMENT_INVOICES_SUCCESS,
    payload,
  };
};

export const getPaymentInvoicesFailure = (
  error?: IErrorModel
): Types.IGetPaymentInvoicesErrorAction => {
  return {
    error,
    type: Consts.GET_PAYMENT_INVOICES_FAILURE,
  };
};

export const createPaymentDetails = (): Types.ICreatePaymentDetailsAction => {
  return {
    type: Consts.CREATE_PAYMENT_DETAILS,
  };
};

export const createPaymentDetailsSuccess = (
  payload: IPaymentModel
): Types.ICreatePaymentDetailsSuccessAction => {
  return {
    type: Consts.CREATE_PAYMENT_DETAILS_SUCCESS,
    payload,
  };
};

export const createPaymentDetailsFailure = (
  error?: IErrorModel
): Types.ICreatePaymentDetailsErrorAction => {
  return {
    error,
    type: Consts.CREATE_PAYMENT_DETAILS_FAILURE,
  };
};

export const getPaymentDetails = (): Types.IGetPaymentDetailsAction => {
  return {
    type: Consts.GET_PAYMENT_DETAILS,
  };
};

export const getPaymentDetailsSuccess = (
  paymentDetails?: IPaymentDetailsModel
): Types.IGetPaymentDetailsSuccessAction => {
  return {
    type: Consts.GET_PAYMENT_DETAILS_SUCCESS,
    payload: paymentDetails,
  };
};

export const getPaymentDetailsFailure = (
  error?: IErrorModel
): Types.IGetPaymentDetailsErrorAction => {
  return {
    error,
    type: Consts.GET_PAYMENT_DETAILS_FAILURE,
  };
};

export const getPaymentOffer = (
  offerId: string
): Types.IGetPaymentOfferAction => {
  return { type: Consts.GET_PAYMENT_OFFER, payload: offerId };
};

export const getPaymentOfferSuccess = (
  offer: IPaymentOfferModel
): Types.IGetPaymentOfferSuccessAction => {
  return { type: Consts.GET_PAYMENT_OFFER_SUCCESS, payload: offer };
};

export const getPaymentOfferError = (
  offerId: string,
  error?: IErrorModel
): Types.IGetPaymentOfferErrorAction => {
  return { type: Consts.GET_PAYMENT_OFFER_FAILURE, error, payload: offerId };
};

export const resetPayment = (): Types.IResetPaymentAction => {
  return {
    type: Consts.RESET_PAYMENT,
  };
};

export const setPaymentCallback = (
  okUrl?: string | UrlObject,
  failUrl?: string | UrlObject
) => {
  return {
    type: Consts.SET_PAYMENT_CALLBACK,
    payload: {
      okUrl,
      failUrl,
    },
  };
};

export const getSwitchOffer = (
  subscription_id: number
): IGetUserSwitchOffersAction => {
  return {
    type: Consts.GET_SWITCH_OFFER,
    payload: { subscription_id },
  };
};

export const getUserSwitchOffersSuccess = (
  payload: IPaymentOfferModel[]
): IGetUserSwitchOffersSuccessAction => {
  return {
    type: Consts.GET_SWITCH_OFFER_SUCCESS,
    payload,
  };
};

export const getUserSwitchOffersFailure = (
  error?: IErrorModel
): IGetUserSwitchOffersFailureAction => {
  return {
    type: Consts.GET_SWITCH_OFFER_FAILURE,
    error,
  };
};

export const switchSubscription = (data: {
  subscription_id: number,
  to_offer_id: string | null}
): ISwitchSubscriptionAction => {
  return {
    type: Consts.SWITCH_SUBSCRIPTION,
    data
  };
};
export const switchSubscriptionSuccess = (
  payload: IPaymentOfferModel[]
): ISwitchSubscriptionSuccessAction => {
  return {
    type: Consts.SWITCH_SUBSCRIPTION_SUCCESS,
    payload,
  };
};
export const switchSubscriptionFailure = (
  error?: IErrorModel
): ISwitchSubscriptionFailureAction => {
  return {
    error,
    type: Consts.SWITCH_SUBSCRIPTION_FAILURE,
  };
};

export const cancelSubscriptionSwitch = (
  subscription_id: number,
): ICancelSubscriptionSwitchAction => {
  return {
    type: Consts.CANCEL_SUBSCRIPTION_SWITCH,
    subscription_id
  };
};

export const cancelSubscriptionSwitchSuccess = (
  payload: IPaymentOfferModel[]
): ICancelSubscriptionSwitchSuccessAction => {
  return {
    type: Consts.CANCEL_SUBSCRIPTION_SWITCH_SUCCESS,
    payload,
  };
};

export const cancelSubscriptionSwitchFailure = (
  error?: IErrorModel
): ICancelSubscriptionSwitchFailureAction => {
  return {
    error,
    type: Consts.CANCEL_SUBSCRIPTION_SWITCH_FAILURE,
  };
};

export const PaymentActions = {
  getPaymentsInfo,
  getPaymentsInfoSuccess,
  getPaymentsInfoFailure,
  createInitialPayment,
  createInitialPaymentSuccess,
  createInitialPaymentFailure,
  createPaymentDetails,
  createPaymentDetailsSuccess,
  createPaymentDetailsFailure,
  createPaymentOrder,
  createPaymentOrderSuccess,
  createPaymentOrderFailure,
  updatePaymentOrder,
  updatePaymentOrderSuccess,
  updatePaymentOrderFailure,
  getPaymentInvoices,
  getPaymentInvoicesSuccess,
  getPaymentInvoicesFailure,
  getPaymentDetails,
  getPaymentDetailsSuccess,
  getPaymentDetailsFailure,
  getPaymentOffer,
  getPaymentOfferSuccess,
  getPaymentOfferError,
  setPaymentCallback,
  switchSubscription,
  switchSubscriptionSuccess,
  switchSubscriptionFailure,
  getSwitchOffer,
  getUserSwitchOffersSuccess,
  getUserSwitchOffersFailure,
  cancelSubscriptionSwitch,
  cancelSubscriptionSwitchSuccess,
  cancelSubscriptionSwitchFailure
};
